// pms-adapters / supabase — the season calendar and the rate profile (0118).
//
// Two adapters, mirroring the two ports and the two lifecycles. Split out of
// `repositories.ts` because that file is already long and these are a coherent
// pair, not because they are special: same scoping, same helpers, same rules.
//
// ── The calendar is READ WHOLE and WRITTEN A PIECE AT A TIME ───────────────
//
// `getCalendar` fetches the header, its periods and its closures in one call —
// that is how the screen reads a year, and three round trips would show it
// assembling itself. Writes are the opposite: one period, one base, one
// closure. The asymmetry is the living-document shape, not an oversight.

import {
  ok,
  type BaseAdjustment,
  type CalendarPeriod,
  type Cents,
  type IsoDate,
  type PeriodPatch,
  type ProposedPeriod,
  type QueryScope,
  type RateProfile,
  type RateSeason,
  type Result,
  type ScopeSource,
  type SeasonCalendarRepository,
  type RateProfileRepository,
  type StoredClosure,
  type StoredRateProfile,
  type StoredSeasonCalendar,
} from '@lemontaigu/pms-core';
import { fromList, fromResult, fromVoid, scopedInsert, scopedQuery } from './support.js';
import type { SupabaseClient } from './support.js';

interface PeriodRow {
  id: number;
  date_from: string;
  date_to: string;
  description: string;
  zones: string | null;
  season: string;
  base_cents: number;
}

interface ClosureRow {
  id: number;
  date_from: string;
  date_to: string;
  reason: string;
}

const rowToPeriod = (r: PeriodRow): CalendarPeriod => ({
  id: r.id,
  from: r.date_from,
  to: r.date_to,
  description: r.description,
  zones: r.zones,
  season: r.season as RateSeason,
  baseCents: r.base_cents as Cents,
});

const rowToClosure = (r: ClosureRow): StoredClosure => ({
  id: r.id,
  from: r.date_from,
  to: r.date_to,
  reason: r.reason,
});

export class SupabaseSeasonCalendarRepository implements SeasonCalendarRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async listCalendars(): Promise<Result<{ id: number; year: number; note: string | null }[]>> {
    return fromList<{ id: number; year: number; note: string | null }, { id: number; year: number; note: string | null }>(
      await scopedQuery(
        this.db.from('pms_season_calendar').select('id, year, note'),
        this.scope.current(),
      ).order('year', { ascending: false }),
      (r) => r,
    );
  }

  async getCalendar(id: number): Promise<Result<StoredSeasonCalendar | null>> {
    const head = await scopedQuery(
      this.db.from('pms_season_calendar').select('id, year, note').eq('id', id),
      this.scope.current(),
    ).maybeSingle();
    if (head.error) return fromResult<StoredSeasonCalendar | null>(head);
    if (head.data === null) return ok(null);

    // Periods and closures in parallel: they are independent lists and the
    // screen needs both before it can render anything.
    const [periods, closures] = await Promise.all([
      scopedQuery(
        this.db
          .from('pms_season_period')
          .select('id, date_from, date_to, description, zones, season, base_cents')
          .eq('calendar_id', id),
        this.scope.current(),
      ).order('date_from'),
      scopedQuery(
        this.db.from('pms_season_closure').select('id, date_from, date_to, reason').eq('calendar_id', id),
        this.scope.current(),
      ).order('date_from'),
    ]);
    if (periods.error) return fromResult<StoredSeasonCalendar | null>(periods);
    if (closures.error) return fromResult<StoredSeasonCalendar | null>(closures);

    const h = head.data as { id: number; year: number; note: string | null };
    return ok({
      id: h.id,
      year: h.year,
      note: h.note,
      periods: ((periods.data ?? []) as PeriodRow[]).map(rowToPeriod),
      closures: ((closures.data ?? []) as ClosureRow[]).map(rowToClosure),
    });
  }

  async createCalendar(year: number, note: string | null): Promise<Result<number>> {
    const r = await this.db
      .from('pms_season_calendar')
      .insert({ ...scopedInsert(this.scope.current()), year, note })
      .select('id')
      .single();
    if (r.error) return fromResult<number>(r);
    return ok((r.data as { id: number }).id);
  }

  deleteCalendar(id: number): Promise<Result<void>> {
    return this.voidWrite('pms_season_calendar', id);
  }

  async addPeriods(calendarId: number, periods: ProposedPeriod[]): Promise<Result<void>> {
    // An empty proposal is a no-op, not a round trip that can only fail.
    if (periods.length === 0) return ok(undefined);
    return fromVoid(
      await this.db.from('pms_season_period').insert(
        periods.map((p) => ({
          ...scopedInsert(this.scope.current()),
          calendar_id: calendarId,
          date_from: p.from,
          date_to: p.to,
          description: p.description,
          zones: p.zones,
          season: p.season,
          base_cents: p.baseCents,
        })),
      ),
    );
  }

  async updatePeriod(id: number, patch: PeriodPatch): Promise<Result<void>> {
    // Only what was asked for. A base tweak must not restate the dates it is
    // not touching — and `base_cents` arriving unchanged would still fire
    // 0118's log trigger's comparison, which is cheap but misleading to read.
    const row: Record<string, unknown> = {};
    if (patch.from !== undefined) row.date_from = patch.from;
    if (patch.to !== undefined) row.date_to = patch.to;
    if (patch.description !== undefined) row.description = patch.description;
    if (patch.zones !== undefined) row.zones = patch.zones;
    if (patch.season !== undefined) row.season = patch.season;
    if (patch.baseCents !== undefined) row.base_cents = patch.baseCents;
    if (Object.keys(row).length === 0) return ok(undefined);

    return fromVoid(
      await scopedQuery(this.db.from('pms_season_period').update(row).eq('id', id), this.scope.current()),
    );
  }

  deletePeriod(id: number): Promise<Result<void>> {
    return this.voidWrite('pms_season_period', id);
  }

  async addClosure(
    calendarId: number,
    from: IsoDate,
    to: IsoDate,
    reason: string,
  ): Promise<Result<void>> {
    return fromVoid(
      await this.db.from('pms_season_closure').insert({
        ...scopedInsert(this.scope.current()),
        calendar_id: calendarId,
        date_from: from,
        date_to: to,
        reason,
      }),
    );
  }

  deleteClosure(id: number): Promise<Result<void>> {
    return this.voidWrite('pms_season_closure', id);
  }

  async baseHistory(periodId: number): Promise<Result<BaseAdjustment[]>> {
    // NOT scoped by property: the log carries no property_code — it hangs off a
    // period, which does. Its RLS is a plain `pms.rate.read`, and the period FK
    // is what confines a row to an établissement.
    return fromList<{ from_cents: number; to_cents: number; changed_at: string }, BaseAdjustment>(
      await this.db
        .from('pms_season_base_adjustment')
        .select('from_cents, to_cents, changed_at')
        .eq('period_id', periodId)
        .order('changed_at', { ascending: false }),
      (r) => ({ fromCents: r.from_cents as Cents, toCents: r.to_cents as Cents, changedAt: r.changed_at }),
    );
  }

  private async voidWrite(table: string, id: number): Promise<Result<void>> {
    return fromVoid(await scopedQuery(this.db.from(table).delete().eq('id', id), this.scope.current()));
  }
}

// ── The profile — 0107's lifecycle, on the half that keeps it ───────────────

interface ProfileRow {
  id: number;
  name: string;
  note: string | null;
  status: string;
  parameters: unknown;
  published_at: string | null;
}

const rowToProfile = (r: ProfileRow): StoredRateProfile => ({
  id: r.id,
  status: r.status as StoredRateProfile['status'],
  publishedAt: r.published_at,
  // `name` and `note` are columns AND live inside the document, so the database
  // can index and freeze them. The COLUMN is authoritative — it is what the
  // unique index and the guard trigger actually see. Same reconciliation
  // `rowToRateParameterSet` does, for the same reason.
  parameters: { ...(r.parameters as RateProfile), name: r.name },
});

export class SupabaseRateProfileRepository implements RateProfileRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async listProfiles(): Promise<Result<StoredRateProfile[]>> {
    return fromList<ProfileRow, StoredRateProfile>(
      await scopedQuery(
        this.db.from('pms_rate_profile').select('id, name, note, status, parameters, published_at'),
        this.scope.current(),
      ).order('id', { ascending: false }),
      rowToProfile,
    );
  }

  async getProfile(id: number): Promise<Result<StoredRateProfile | null>> {
    const r = await scopedQuery(
      this.db
        .from('pms_rate_profile')
        .select('id, name, note, status, parameters, published_at')
        .eq('id', id),
      this.scope.current(),
    ).maybeSingle();
    if (r.error) return fromResult<StoredRateProfile | null>(r);
    return ok(r.data === null ? null : rowToProfile(r.data as ProfileRow));
  }

  async createProfileDraft(draft: RateProfile, note: string | null): Promise<Result<number>> {
    const r = await this.db
      .from('pms_rate_profile')
      .insert({ ...scopedInsert(this.scope.current()), name: draft.name, note, parameters: stripName(draft) })
      .select('id')
      .single();
    if (r.error) return fromResult<number>(r);
    return ok((r.data as { id: number }).id);
  }

  async updateProfileDraft(id: number, draft: RateProfile, note: string | null): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(
        this.db
          .from('pms_rate_profile')
          .update({ name: draft.name, note, parameters: stripName(draft) })
          .eq('id', id),
        this.scope.current(),
      ),
    );
  }

  publishProfile(id: number): Promise<Result<void>> {
    return this.setStatus(id, 'published');
  }

  archiveProfile(id: number): Promise<Result<void>> {
    return this.setStatus(id, 'archived');
  }

  async deleteProfileDraft(id: number): Promise<Result<void>> {
    return fromVoid(
      await scopedQuery(this.db.from('pms_rate_profile').delete().eq('id', id), this.scope.current()),
    );
  }

  private async setStatus(id: number, status: string): Promise<Result<void>> {
    // `published_at` is stamped by the trigger, never by the caller: the pair
    // (status, published_at) is what a provenance lookup reads, and letting
    // them disagree would make it unanswerable exactly when it is asked.
    return fromVoid(
      await scopedQuery(this.db.from('pms_rate_profile').update({ status }).eq('id', id), this.scope.current()),
    );
  }
}

/** The document without its name — the column holds that, and storing it twice
 *  is how the two quietly diverge. */
function stripName(p: RateProfile): Omit<RateProfile, 'name'> {
  const { name: _name, ...rest } = p;
  return rest;
}
